/**
 * useLocalizedRoutes - Prefetch Route Slugs for Current Locale
 * 
 * Fetches all route slugs for the current locale at once,
 * providing synchronous access to localized paths via getPath().
 * 
 * This replaces the manifest-based approach with DB-first caching.
 * 
 * @example
 * const { getPath, isLoading } = useLocalizedRoutes();
 * const expertsPath = getPath('experts'); // '/expertos' in es-ES
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLocale } from '@/contexts/LocaleContext';
import { usePrimaryLocale } from './usePrimaryLocale';
import { logger } from '@/utils/logger';
import { FUNCTIONAL_ROUTE_IDS } from '@/constants/routing';

// ============================================================================
// TYPES
// ============================================================================

interface RouteSlugMap {
  [routeKey: string]: string;
}

interface UseLocalizedRoutesResult {
  /** Get localized path for a route key */
  getPath: (routeKey: string) => string;
  /** Get localized path for a page slug (institutional pages) */
  getPagePath: (pageSlug: string) => string;
  /** Whether route data is still loading */
  isLoading: boolean;
  /** Whether route data is ready */
  isReady: boolean;
  /** Any error that occurred */
  error: Error | null;
}

// ============================================================================
// FETCH FUNCTION
// ============================================================================

/**
 * Fetch all route and page slugs for a given locale
 * Uses primaryLocale for DB queries (localization_metadata uses primary locales like 'es', not regional like 'es-ES')
 */
async function fetchLocalizedRoutes(primaryLocale: string): Promise<{
  routes: RouteSlugMap;
  pages: RouteSlugMap;
}> {
  const startTime = performance.now();

  if (!primaryLocale) {
    return { routes: {}, pages: {} };
  }

  try {
    // EN Row as SOT: Fetch translated_slug from current locale
    // For EN locale, this IS the source; for non-EN, it's the translation
    const [localeData, englishOriginals] = await Promise.all([
      supabase
        .from('localization_metadata')
        .select('source_type, source_id, translated_slug')
        .eq('locale', primaryLocale)
        .in('source_type', ['route', 'page']),
      // Fetch English translated_slug for pages to enable footer lookups
      // Footer uses English keys like "about-us" which need to map to localized slugs
      supabase
        .from('localization_metadata')
        .select('source_id, translated_slug')
        .eq('locale', 'en')
        .eq('source_type', 'page')
        .not('translated_slug', 'is', null)
    ]);

    if (localeData.error) {
      logger.error('[useLocalizedRoutes] DB query failed', {
        primaryLocale,
        error: localeData.error.message,
      });
      throw new Error(localeData.error.message);
    }

    const routes: RouteSlugMap = {};
    const pages: RouteSlugMap = {};

    if (localeData.data) {
      for (const row of localeData.data) {
        if (row.source_type === 'route' && row.translated_slug) {
          routes[row.source_id] = row.translated_slug;
        } else if (row.source_type === 'page' && row.translated_slug) {
          // Map by source_id (UUID)
          pages[row.source_id] = row.translated_slug;
          // Map by translated_slug (self-reference for current locale)
          pages[row.translated_slug] = row.translated_slug;
        }
      }
    }

    // Map English slug → localized slug for footer lookups
    // EN Row as SOT: The English row's translated_slug IS the original/source slug
    if (englishOriginals.data) {
      for (const orig of englishOriginals.data) {
        // The English slug is in translated_slug (EN row is the source)
        if (orig.translated_slug && pages[orig.source_id]) {
          // Map English key to the localized slug
          pages[orig.translated_slug] = pages[orig.source_id];
        }
      }
    }

    if (process.env.NODE_ENV === 'development') {
      logger.debug('[useLocalizedRoutes] Fetched slugs', {
        primaryLocale,
        routeCount: Object.keys(routes).length,
        pageCount: Object.keys(pages).length,
        timeMs: Math.round(performance.now() - startTime),
      });
    }

    return { routes, pages };
  } catch (err) {
    logger.error('[useLocalizedRoutes] Unexpected error', { primaryLocale, error: err });
    throw err;
  }
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to get localized route paths for the current locale
 * 
 * Prefetches all route slugs on mount, providing O(1) synchronous lookups.
 * Falls back to the route key if slug not found (for undefined routes).
 */
export function useLocalizedRoutes(): UseLocalizedRoutesResult {
  const { settings } = useLocale();
  const locale = settings.locale;
  
  // Resolve regional locale (es-ES) to primary locale (es) for DB queries
  // Note: resolveToPrimary has built-in bootstrap fallback using split('-')[0] during loading
  const { resolveToPrimary } = usePrimaryLocale();
  const primaryLocale = resolveToPrimary(locale);

  const {
    data,
    isLoading: isQueryLoading,
    error,
  } = useQuery({
    queryKey: ['localized-routes', primaryLocale],
    queryFn: () => fetchLocalizedRoutes(primaryLocale),
    // Don't block on isPrimaryLoading - resolveToPrimary has bootstrap fallback
    enabled: !!primaryLocale,
    staleTime: 10 * 60 * 1000, // 10 minutes - routes rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const isLoading = isQueryLoading;
  const routes = data?.routes || {};
  const pages = data?.pages || {};

  /**
   * Get localized path for a route key
   * Falls back to the route key if not found
   */
  const getPath = (routeKey: string): string => {
    // Functional routes always use EN slug — not indexable, system-critical
    if (FUNCTIONAL_ROUTE_IDS.has(routeKey)) {
      return `/${routeKey}`;
    }
    const slug = routes[routeKey];
    return slug ? `/${slug}` : `/${routeKey}`;
  };

  /**
   * Get localized path for a page (institutional page)
   * Supports both English slugs (e.g., "about-us") and source IDs
   */
  const getPagePath = (pageSlug: string): string => {
    const slug = pages[pageSlug];
    return slug ? `/${slug}` : `/${pageSlug}`;
  };

  return {
    getPath,
    getPagePath,
    isLoading,
    isReady: !isLoading && !!data,
    error: error as Error | null,
  };
}
